import { isEqual } from "lodash-es";
import { useEffect, useRef } from "react";
import { type RouteComponentProps } from "react-router";

import { routes } from "utilities/routes";

/**
 * When the left-nav JobPicker selection changes after mount, redirect to the warranty
 * list for the newly selected job. Mirrors the legacy modal's outside-click behavior:
 * switching jobs should abandon the current warranty view (add or details) rather than
 * silently leaving a stale-jobId form under a header that reflects the new job.
 *
 * The ref guards the initial render so deep-link loads where stored picker state differs
 * from the URL jobId do not trigger a false redirect.
 *
 * Any genuine post-mount change to `jobIds` redirects to the warranty list; because the
 * list route is `Multiple` mode and reflects the picker, the single static target lands on
 * the All Jobs list (AC #2) or the chosen job's list (AC #3 — including the claim's own job).
 * The empty-`jobIds` case is still skipped because it is a transient picker-reload artifact,
 * not a user selection. This mirrors `DailyLogView`'s redirect-on-any-change effect.
 *
 * Used on both `/Warranty/:id/:jobId` (details) and `/WarrantyAdd/:jobId` (add) routes.
 */
export const useRedirectOnJobPickerSwitch = (
    jobIds: number[],
    history: RouteComponentProps["history"],
) => {
    const previousJobIdsRef = useRef<number[] | undefined>(undefined);
    useEffect(() => {
        const previous = previousJobIdsRef.current;
        // Update the ref unconditionally before deciding whether to redirect, so a stale
        // `previous` can't trigger the redirect twice if the host happens not to unmount
        // (e.g., a future caller that uses this hook in a place where the redirect target
        // is the same component, or React StrictMode double-invocation in dev).
        previousJobIdsRef.current = jobIds;
        if (previous !== undefined && !isEqual(previous, jobIds)) {
            // Empty `jobIds` is the AllJobs→NoJobs fallback the picker can emit transiently
            // during a picker reload, not a user choice — never treat it as a switch.
            if (jobIds.length === 0) {
                return;
            }
            history.replace(routes.warranty.getListLink());
        }
    }, [jobIds, history]);
};
